import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { type MenuOption } from '@saviynt/design-system';

import { type ActiveFiltersState } from '../../../shared/types';
import { type FilterDeltaState } from '../data-layer/violationsTabTypes';
import { violationsTabActions } from '../data-store/violationsTabSlice';

/**
 * Calculates the difference between previous and current filter states
 *
 * @template T - Generic type extending either ActiveFiltersState[] or MenuOption[]
 * @param {T} prevActiveFilters - Previous state of active filters
 * @param {T} currentActiveFilters - Current state of active filters
 * @param {boolean} [isOption=false] - Flag to indicate if processing options
 * @returns {FilterDeltaState} Object containing added and cleaned filters
 */
const getFilterDelta = <T extends ActiveFiltersState[] | MenuOption[]>(
  prevActiveFilters: T,
  currentActiveFilters: T,
  isOption = false
): FilterDeltaState => {
  // Isolating the keys
  const initialArrayKeys = prevActiveFilters.map((filter) => filter.value);
  const finalArrayKeys = currentActiveFilters.map((filter) => filter.value);

  // Array of keys of which filters added or removed
  const FILTER_KEYS_ADDED = finalArrayKeys.filter(
    (key) => !initialArrayKeys.includes(key)
  );
  const FILTER_KEYS_CLEANED = initialArrayKeys.filter(
    (key) => !finalArrayKeys.includes(key)
  );
  const UNCOMMON_KEYS = [...FILTER_KEYS_ADDED, ...FILTER_KEYS_CLEANED];

  // Array of filters which are common to both states
  const initialInSituArray = prevActiveFilters.filter(
    (filter) => !UNCOMMON_KEYS.includes(filter.value)
  ) as ActiveFiltersState[];
  const finalInSituArray = currentActiveFilters.filter(
    (filter) => !UNCOMMON_KEYS.includes(filter.value)
  ) as ActiveFiltersState[];

  // Logic to check whether options should be compared or not
  const shouldIterateOverOptions = Boolean(
    initialInSituArray.length > 0 &&
      finalInSituArray.length > 0 &&
      initialInSituArray.length === finalInSituArray.length &&
      !isOption
  );

  // Handling previous and current filters options for common filters
  const optionMap: {
    FILTERS_ADDED: ActiveFiltersState[];
    FILTERS_CLEANED: ActiveFiltersState[];
  } = {
    FILTERS_ADDED: [],
    FILTERS_CLEANED: [],
  };

  if (shouldIterateOverOptions) {
    const filterLength = finalInSituArray.length || initialInSituArray.length;

    for (let i = 0; i < filterLength; i += 1) {
      const initialOptions = initialInSituArray[i].options;
      const finalOptions = finalInSituArray[i].options;
      const response = getFilterDelta(initialOptions, finalOptions, true);

      const filter = finalInSituArray[i];

      if (response.FILTERS_ADDED.length > 0) {
        optionMap.FILTERS_ADDED = [
          ...optionMap.FILTERS_ADDED,
          { ...filter, options: response.FILTERS_ADDED },
        ] as ActiveFiltersState[];
      }

      if (response.FILTERS_CLEANED.length > 0) {
        optionMap.FILTERS_CLEANED = [
          ...optionMap.FILTERS_CLEANED,
          { ...filter, options: response.FILTERS_CLEANED },
        ] as ActiveFiltersState[];
      }
    }
  }

  return {
    FILTERS_ADDED: [
      ...(currentActiveFilters.filter((filter) =>
        FILTER_KEYS_ADDED.includes(filter.value)
      ) as ActiveFiltersState[]),
      ...optionMap.FILTERS_ADDED,
    ],
    FILTERS_CLEANED: [
      ...prevActiveFilters.filter((filter) =>
        FILTER_KEYS_CLEANED.includes(filter.value)
      ),
      ...optionMap.FILTERS_CLEANED,
    ] as ActiveFiltersState[],
  };
};

/**
 * Custom hook to manage filter delta state in the violations tab
 *
 * @returns {{ setDelta: <T extends ActiveFiltersState[]>(prevActiveFilters: T, currentActiveFilters: T) => void }}
 *          Object containing setDelta function
 *
 * @example
 * const { setDelta } = useSetFilterDelta();
 * setDelta(previousFilters, newFilters);
 */
const useSetFilterDelta = () => {
  const dispatch = useDispatch();

  /**
   * Updates the filter delta state and active filters in the store
   *
   * @template T - Generic type extending ActiveFiltersState[]
   * @param {T} prevActiveFilters - Previous state of active filters
   * @param {T} currentActiveFilters - Current state of active filters
   */
  const setDelta = useCallback(
    <T extends ActiveFiltersState[]>(
      prevActiveFilters: T,
      currentActiveFilters: T
    ) => {
      const filterDelta = getFilterDelta(
        prevActiveFilters,
        currentActiveFilters
      );

      dispatch(violationsTabActions.setFiltersDelta(filterDelta));
      dispatch(violationsTabActions.setActiveFilters(currentActiveFilters));
    },
    [dispatch]
  );

  return { setDelta };
};

// eslint-disable-next-line import/prefer-default-export
export { useSetFilterDelta };
